import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api, consulta } from '../api/cliente';
import type { Pagina } from '../api/cliente';
import {
  Aviso,
  Campo,
  Cargando,
  Encabezado,
  Etiqueta,
  Paginador,
  Tarjeta,
  Vacio,
} from '../ui/piezas';

interface Propietario {
  id: string;
  tipoPersona: string;
  nombreCompleto: string;
  documento: string | null;
  email: string | null;
  telefono: string | null;
  ciudad: string | null;
  sede: string;
  banco: string | null;
  cuentaUltimos4: string | null;
  tieneDatosBancarios: boolean;
  retencionAplica: boolean;
  estado: string;
}

export default function Propietarios() {
  const [q, setQ] = useState('');
  const [pagina, setPagina] = useState(0);

  const listado = useQuery({
    queryKey: ['propietarios', q, pagina],
    queryFn: () =>
      api<Pagina<Propietario>>(`/admin/propietarios${consulta({ q, page: pagina, size: 25 })}`),
  });

  const filas = listado.data?.content ?? [];

  return (
    <>
      <Encabezado
        titulo="Propietarios"
        nota="Los dueños de lo que administramos. No son usuarios del sistema."
      />

      <Aviso error={listado.error} />

      <Tarjeta>
        <div style={{ maxWidth: 380, marginBottom: 16 }}>
          <Campo
            etiqueta="Buscar"
            ayuda="Por nombre o documento, sin distinguir mayúsculas ni tildes"
            valor={q}
            alCambiar={(v) => {
              setQ(v);
              setPagina(0);
            }}
            placeholder="Peña, 1020304050…"
          />
        </div>

        {listado.isPending ? (
          <Cargando />
        ) : filas.length === 0 ? (
          <Vacio>No hay propietarios con esa búsqueda.</Vacio>
        ) : (
          <div className="tabla-marco">
            <table>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Documento</th>
                  <th>Contacto</th>
                  <th>Oficina</th>
                  <th>Pago</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <div>{p.nombreCompleto}</div>
                      <small style={{ opacity: 0.7 }}>{p.tipoPersona.toLowerCase()}</small>
                    </td>
                    <td>{p.documento ?? '—'}</td>
                    <td>{[p.email, p.telefono].filter(Boolean).join(' · ') || '—'}</td>
                    <td>{p.sede}</td>
                    <td>
                      {p.tieneDatosBancarios
                        ? `${p.banco ?? 'cuenta'} ····${p.cuentaUltimos4 ?? ''}`
                        : 'sin registrar'}
                    </td>
                    <td>
                      <Etiqueta>{p.estado}</Etiqueta>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Paginador
          pagina={listado.data?.number ?? 0}
          total={listado.data?.totalElements ?? 0}
          paginas={listado.data?.totalPages ?? 0}
          alCambiar={setPagina}
        />
        <p className="campo__ayuda" style={{ marginTop: 10 }}>
          El número de cuenta completo no viaja en este listado: solo lo devuelve un endpoint
          aparte, y esa lectura queda auditada.
        </p>
      </Tarjeta>
    </>
  );
}
